import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { InviteKey, InviteKeySchema, LiveId, LiveIdSchema, SpaceKey, SpaceKeySchema, UserId, UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { assertNever } from "../lib/assert-never"

export const InviteKindSpace = z.literal("space")
export const InviteKindFriend = z.literal("friend")
export const InviteKindLiveViewerSpace = z.literal("live-viewer-space")

export const InviteKind = z.union([
  InviteKindSpace,
  InviteKindFriend,
  InviteKindLiveViewerSpace,
]).describe("招待の種別。")

export type InviteKind = z.infer<typeof InviteKind>

export const InviteStatusActive = z.literal("active")
export const InviteStatusDeactivated = z.literal("deactivated")
export const InviteStatusRejected = z.literal("rejected")
export const InviteStatusAccepted = z.literal("accepted")

export const InviteStatus = z.union([
  InviteStatusActive,
  InviteStatusDeactivated,
  InviteStatusRejected,
  InviteStatusAccepted,
]).describe("招待の状態。")

export type InviteStatus = z.infer<typeof InviteStatus>

export const InviteSpaceResponse = z.object({
  kind: InviteKindSpace,
  spaceKey: SpaceKeySchema
})

export type InviteSpaceResponse = z.infer<typeof InviteSpaceResponse>

export const InviteFriendResponse = z.object({
  kind: InviteKindFriend,
  userId: UserIdSchema
})

export type InviteFriendResponse = z.infer<typeof InviteFriendResponse>

export const InviteLiveViewerSpaceResponse = z.object({
  kind: InviteKindLiveViewerSpace,
  spaceKey: SpaceKeySchema,
  liveId: LiveIdSchema,
  inviteKey: InviteKeySchema,
})

export type InviteLiveViewerSpaceResponse = z.infer<typeof InviteLiveViewerSpaceResponse>

export const InviteResponse = z.union([
  InviteSpaceResponse,
  InviteFriendResponse,
  InviteLiveViewerSpaceResponse,
])

export type InviteResponse = z.infer<typeof InviteResponse>


export const InviteDocumentCommon = z.object({
  expired_at_seconds: z.number().describe("有効期限（秒数）。"),
  limit: z.number().describe("使用可能回数。"),
  count: z.number().describe("使用回数。"),
  status: InviteStatus
})

export const InviteSpaceDocumentMeta = z.object({
  kind: InviteKindSpace,
  user_id: UserIdSchema,
  space_key: SpaceKeySchema
}).extend(InviteDocumentCommon.shape)

export const InviteFriendDocumentMeta = z.object({
  kind: InviteKindFriend,
  user_id: UserIdSchema
}).extend(InviteDocumentCommon.shape)

export const InviteLiveViewerSpaceDocumentMeta = z.object({
  kind: InviteKindLiveViewerSpace,
  user_id: UserIdSchema,
  target_user_id: UserIdSchema,
  space_key: SpaceKeySchema,
  live_id: LiveIdSchema,
}).extend(InviteDocumentCommon.shape)

export const InviteDocumentMeta = z.union([
  InviteSpaceDocumentMeta,
  InviteFriendDocumentMeta,
  InviteLiveViewerSpaceDocumentMeta,
])

export type InviteDocumentMeta = z.infer<typeof InviteDocumentMeta>

export const InviteDocumentBody = z.intersection(
  InviteDocumentMeta,
  z.object({
    expires_at: z.number().nullable().describe("有効期限の絶対時刻（UNIXタイムスタンプミリ秒）。created_at + expired_at_seconds * 1000 で計算される。"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export const InviteDocument = z.intersection(
  InviteDocumentBody,
  z.object({
    id: InviteKeySchema,
  })
)

export type InviteDocument = z.infer<typeof InviteDocument>

export const CreateOrUpdateInviteRequest = z.object({
  expiredAtSeconds: z.int().min(1),
  limit: z.int().min(1),
})

export type CreateOrUpdateInviteRequest = z.infer<typeof CreateOrUpdateInviteRequest>

export const CreateLiveViewerSpaceInviteRequest = z.object({
  kind: InviteKindLiveViewerSpace,
  userId: UserIdSchema,
  liveId: LiveIdSchema,
  expiredAtSeconds: z.int().min(1),
  limit: z.int().min(1),
})

export type CreateLiveViewerSpaceInviteRequest = z.infer<typeof CreateLiveViewerSpaceInviteRequest>

export const UpdateInviteStatusRequest = z.object({
  status: InviteStatusRejected,
})

export type UpdateInviteStatusRequest = z.infer<typeof UpdateInviteStatusRequest>

export class InviteDocuments {

  static isExpired(doc: InviteDocument): boolean {
    if (doc.expires_at != null) {
      return doc.expires_at <= Date.now()
    }
    if (doc.expired_at_seconds === null) {
      return false
    }
    return doc.created_at + doc.expired_at_seconds * 1000 <= Date.now()
  }

  static toDocumentData(doc: InviteDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): InviteDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const baseObject = {
      id: InviteKey(doc.id),
      kind: data["kind"],
      expired_at_seconds: data["expired_at_seconds"] == null ? null : data["expired_at_seconds"],
      expires_at: data["expires_at"] ?? (data["expired_at_seconds"] != null && data["created_at"] != null ? data["created_at"] + data["expired_at_seconds"] * 1000 : null),
      limit: data["limit"] == null ? null : data["limit"],
      count: data["count"],
      status: data["status"] ?? InviteStatusDeactivated, // status はあとから追加したものなので存在しないときはdeactivatedとみなす
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    const kind = baseObject.kind as InviteKind | undefined
    switch (kind) {
      case InviteKindFriend.value:
        return {
          ...baseObject,
          user_id: UserId(data["user_id"]),
        }
      case InviteKindSpace.value:
        return {
          ...baseObject,
          user_id: UserId(data["user_id"]),
          space_key: SpaceKey(data["space_key"]),
        }
      case InviteKindLiveViewerSpace.value:
        return {
          ...baseObject,
          user_id: UserId(data["user_id"]),
          target_user_id: UserId(data["target_user_id"]),
          space_key: SpaceKey(data["space_key"]),
          live_id: LiveId(data["live_id"]),
        }
      case undefined:
        // もともとinviteはspaceの招待にしか使われていなかった。kindをあとから足したので、ない場合はspaceの招待とみなす
        return {
          ...baseObject,
          kind: "space",
          user_id: UserId(data["user_id"]),
          space_key: SpaceKey(data["space_key"]),
        }
      default:
        return assertNever(kind)
    }
  }
}
